import { api } from '@/lib/api';
import type { BusinessHoursDay, ClinicData, CnpjLookupResult } from '@valiosascrm/shared';

export type { BusinessHoursDay, ClinicData };

export interface HolidayBlock {
  date: string;
  reason: string;
}

export interface ClinicSettings {
  businessHours?: Record<string, BusinessHoursDay>;
  holidays?: HolidayBlock[];
  defaults?: {
    sessionDuration: number;
    bufferTime: number;
    timezone: string;
  };
  clinicData?: ClinicData;
  /**
   * Story 67.8 — o nome da clínica (`Tenant.name`), a única chave do nome. Vem no GET/PUT e
   * grava só por `setClinicName`; o PUT não o guarda.
   */
  clinicName?: string;
  // Lembretes de consulta + confirmação (F15)
  remindersEnabled?: boolean;
  reminder24h?: boolean;
  reminder1h?: boolean;
  reminder24hProfessional?: boolean;
  reminder1hProfessional?: boolean;
  confirmationEnabled?: boolean;
  confirmationHoursBefore?: number;
  // RC-168: botões Confirmar/Reagendar na confirmação (sem a chave = ligados). Grava só por
  // `setConfirmationButtons`, que é da administradora.
  confirmationButtonsEnabled?: boolean;
  // Hora local (0-23) dos envios diários (aniversário/reativação)
  dailyAutomationHour?: number;
  // F16: Post-appointment follow-up
  followUpEnabled?: boolean;
  // Onda E-2 (H2/H1): pausáveis no painel único de Mensagens (default ligados).
  cancellationEnabled?: boolean;
  birthdayEnabled?: boolean;
  followUpDelayDays?: number;
  // F16: Inactive reactivation
  reactivationEnabled?: boolean;
  reactivationMonths?: number;
  // Story 61.5 — janela de espera por pessoa (defaults: 30 dias, 2 mensagens, não reenviar a quem respondeu,
  // incluir quem não tem atendimento registrado = comportamento anterior).
  reactivationCooldownDays?: number;
  reactivationMaxAttempts?: number;
  reactivationSkipIfReplied?: boolean;
  reactivationRequireHistory?: boolean;
  /** Só na resposta do PUT (61.3): lotes automáticos cancelados ao desligar uma automação. */
  cancelledCampaigns?: number;
  // Story 22.2: NPS Survey
  npsEnabled?: boolean;
  npsDelayDays?: number;
  npsTokenExpiryDays?: number;
  // Story 44.9 [ACR-020]: base da comissão por tenant (default 'gross' quando ausente)
  commission?: { base: 'gross' | 'net' };
  /** Validade, em minutos, do link temporário de foto clínica e de PDF de documento. */
  mediaLinkMinutes?: number;
  /**
   * RC-040 / Story 49.2: CNPJ da clínica — consumido pela variável {{clinica.cnpj}} de
   * documentos (contrato de pacote). Opcional: sem ele, a emissão de um modelo que USE a
   * variável é recusada nomeando o que falta. String vazia limpa o campo no servidor.
   */
  clinicCnpj?: string;
}

/** As mesmas opções que o servidor aceita — valor fora daqui é recusado no PUT. */
export const MEDIA_LINK_OPTIONS = [
  { minutos: 15, rotulo: '15 minutos' },
  { minutos: 60, rotulo: '1 hora' },
  { minutos: 240, rotulo: '4 horas (recomendado)' },
  { minutos: 480, rotulo: '8 horas' },
  { minutos: 720, rotulo: '12 horas' },
] as const;

export const MEDIA_LINK_DEFAULT_MINUTES = 240;

export async function getClinicSettings(): Promise<ClinicSettings> {
  const { data } = await api.get<ClinicSettings>('/settings/clinic');
  return data;
}

export async function updateClinicSettings(settings: ClinicSettings): Promise<ClinicSettings> {
  const { data } = await api.put<ClinicSettings>('/settings/clinic', settings);
  return data;
}

export async function setClinicName(name: string): Promise<{ name: string }> {
  const { data } = await api.patch<{ name: string }>('/settings/clinic-name', { name });
  return data;
}

export async function setConfirmationButtons(enabled: boolean): Promise<{ enabled: boolean }> {
  const { data } = await api.patch<{ enabled: boolean }>('/settings/confirmation-buttons', { enabled });
  return data;
}

/**
 * Story 50.2 — consulta assistida do CNPJ na Receita.
 *
 * A rota NUNCA devolve erro HTTP: o contrato é sempre 200 com um `CnpjLookupResult`.
 * Isso é deliberado — se ela começasse a devolver 4xx/5xx, a tela ganharia um caminho de
 * exceção e o AC3 (não bloquear o salvamento) morreria em silêncio.
 */
export async function lookupCnpj(cnpj: string): Promise<CnpjLookupResult> {
  const { data } = await api.get<CnpjLookupResult>('/settings/clinic/cnpj-lookup', {
    params: { cnpj },
  });
  return data;
}
